import apiClient from './axios';
import { ApiResponse } from '../types';

export interface MandateMember {
  id: string;
  subjectType: 'director' | 'member';
  displayName: string;
  /** Null means no personal cap. `"0"` means never alone — a real clause,
   *  and a different statement. */
  soloLimitKobo: string | null;
}

export interface MandatePair {
  id: string;
  firstMemberId: string;
  secondMemberId: string;
}

export interface PendingMandate {
  mandate: {
    id: string;
    businessId: string;
    version: number;
    jointRule: 'any_two' | 'named_pairs';
    /** Above this, one signature is not enough. Null means never. */
    jointThresholdKobo: string | null;
    /** The board resolution it claims to copy. */
    documentId: string | null;
    createdAt: string;
  };
  members: MandateMember[];
  pairs: MandatePair[];
  /**
   * Present when the business wrote the resolution in PayIQ rather than
   * uploading one. The resolution is generated from the mandate, so there is
   * nothing to compare — what needs a person is why it did not come into
   * force on its own, and the signatures themselves.
   */
  resolution: ResolutionEvidence | null;
}

export interface ResolutionEvidence {
  resolutionId: string;
  /** Why it came to review: an uploaded signature, an unverified signer… */
  reviewReason: string | null;
  signatures: {
    name: string;
    /** How it was given, in words. */
    how: string;
    signedAt: string;
    /** A hand-signed copy the director uploaded from their link. */
    uploadedCopyUrl: string | null;
    /** An image of their signature — their own, or one uploaded for them. */
    signatureImageUrl: string | null;
  }[];
}

export const signingMandatesApi = {
  pending: async () => {
    const { data } =
      await apiClient.get<ApiResponse<PendingMandate[]>>(
        '/admin/signing-mandates/pending',
      );
    return data.data;
  },

  /**
   * Approving is the moment a board resolution starts deciding who can move
   * that company's money — until then the transfer path ignores the mandate
   * entirely. Rejecting takes a note, because "rejected" alone sends a
   * business back to a document they believe is correct.
   */
  review: async (id: string, decision: 'approve' | 'reject', note?: string) => {
    const { data } = await apiClient.post<ApiResponse<{ status: string }>>(
      `/admin/signing-mandates/${id}/review`,
      { decision, note },
    );
    return data.data;
  },
};
